import { Team, TeamState } from '@/types';

type TeamOrTeamState = Team | TeamState;

interface TeamLogoProps {
  team: TeamOrTeamState;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

const sizeMap = {
  xs: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-16 h-16',
  '2xl': 'w-24 h-24',
};

export function TeamLogo({ team, size = 'sm', className = '' }: TeamLogoProps) {
  const isLarge = size === 'xl' || size === '2xl';

  if (team?.logoUrl) {
    return (
      <img
        src={team.logoUrl}
        alt={team.name}
        loading="lazy"
        className={`${sizeMap[size]} rounded-full object-cover flex-shrink-0 ${className}`}
        onError={e => {
          // Fallback to color dot on error
          (e.target as HTMLImageElement).style.display = 'none';
          const parent = (e.target as HTMLImageElement).parentElement;
          if (parent && !parent.querySelector('.fallback-dot')) {
            const dot = document.createElement('span');
            dot.className = `fallback-dot ${sizeMap[size]} rounded-full flex-shrink-0`;
            dot.style.backgroundColor = team.color;
            if (isLarge) {
              dot.style.border = '3px solid rgba(255,255,255,0.3)';
            }
            parent.insertBefore(dot, e.target as HTMLImageElement);
          }
        }}
      />
    );
  }

  return (
    <span
      className={`${sizeMap[size]} rounded-full flex-shrink-0 border ${isLarge ? 'border-4 border-white/30' : 'border border-white/20'} ${className}`}
      style={{ backgroundColor: team?.color }}
    />
  );
}

interface TeamDisplayProps {
  team: TeamOrTeamState;
  showLogo?: boolean;
  showShortName?: boolean;
  logoSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

export function TeamDisplay({ team, showLogo = true, showShortName = false, logoSize = 'sm', className = '' }: TeamDisplayProps) {
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {showLogo && <TeamLogo team={team} size={logoSize} />}
      <span className="font-medium" style={{ color: team.color }}>
        {showShortName ? team.shortName : team.name}
      </span>
    </div>
  );
}
